import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/** Body for "Structured logging best practices" guide. */
export function StructuredLoggingGuideBody() {
  return (
    <>
      <Lead>
        Structured logging means emitting logs as machine-readable data, typically
        JSON key-value pairs, instead of free-form text. It is the single biggest
        thing you can do to make logs searchable, alertable, and useful at scale.
        This guide explains what structured logging is, why it matters, and the
        practices that make it pay off.
      </Lead>

      <GuideSection id="what-is-it" title="What is structured logging?">
        <P>
          A <Term>structured log</Term> records an event as discrete fields rather
          than a sentence. Compare an unstructured line:
        </P>
        <P>
          <Term>User 4821 failed login from 10.0.0.5 after 3 attempts</Term>
        </P>
        <P>with its structured equivalent:</P>
        <P>
          <Term>
            {'{ "event": "login_failed", "user_id": 4821, "src_ip": "10.0.0.5", "attempts": 3, "level": "warn" }'}
          </Term>
        </P>
        <P>
          The information is the same, but the structured version can be filtered,
          grouped and aggregated by field, such as <Term>user_id</Term>, <Term>src_ip</Term>{' '}
          or <Term>attempts</Term>, without fragile text parsing.
        </P>
      </GuideSection>

      <GuideSection id="why" title="Why it matters">
        <UL>
          <LI><Term>Queryable</Term>: filter and aggregate by field instead of grepping text.</LI>
          <LI><Term>Reliable parsing</Term>: no brittle regex to extract values that move around in a sentence.</LI>
          <LI><Term>Better detections &amp; alerts</Term>: security and KPI rules can reference precise fields.</LI>
          <LI><Term>Correlation</Term>: shared IDs let you trace a request across services.</LI>
          <LI><Term>Cost &amp; speed</Term>: structured data compresses and queries better in a columnar store.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="best-practices" title="Structured logging best practices">
        <OL>
          <LI><Term>Use a consistent schema</Term>: agree on field names (e.g. <Term>user_id</Term>, not <Term>uid</Term> in one service and <Term>userId</Term> in another).</LI>
          <LI><Term>Log in JSON</Term>: the de facto machine-readable format, widely supported by logging libraries and collectors.</LI>
          <LI><Term>Use proper levels</Term>: debug, info, warn, error, fatal, applied consistently so severity is queryable.</LI>
          <LI><Term>Add context fields</Term> like service, environment, version and region, so you can slice by them.</LI>
          <LI><Term>Include correlation IDs</Term>: a request/trace ID on every related log line ties a flow together.</LI>
          <LI><Term>Use accurate, synchronised timestamps</Term>: ISO-8601 in UTC from a synced clock.</LI>
          <LI><Term>Keep messages stable</Term>: put the variable data in fields, not in an ever-changing message string.</LI>
          <LI><Term>Mind PII</Term>: avoid logging secrets and personal data, or redact it before storage (see the GDPR angle below).</LI>
        </OL>
      </GuideSection>

      <GuideSection id="structured-vs-unstructured" title="Structured vs. unstructured (and OpenTelemetry)">
        <P>
          Unstructured logs are easy to write and human-readable, but they force
          expensive, fragile parsing downstream and resist aggregation. Structured
          logs cost a little discipline at write time and pay it back at query time.
          Standards like <Term>OpenTelemetry (OTLP)</Term> push structured,
          consistently-attributed telemetry as the default, a good direction to
          align with.
        </P>
        <Callout title="If you can't change the source">
          <p>
            Not every system emits clean JSON. A log pipeline can parse and structure
            messy logs at ingest, extracting fields, normalising names, and redacting
            PII, so you get the benefits even from legacy sources.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse handles structured logs">
        <P>
          LogPulse ingests structured logs natively (JSON and OTLP) and gives you{' '}
          visual pipelines to parse, normalise and redact less-structured sources at
          ingest. LPQL then queries nested fields directly with JSON-path navigation,
          and PII can be redacted before storage to support GDPR data-minimisation.
          See{' '}
          <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>,{' '}
          <GuideLink href="/ai-log-search">AI log search</GuideLink>, and{' '}
          <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>{' '}
          for the data-minimisation and retention side.
        </P>
      </GuideSection>
    </>
  );
}
